/*
    Don't flex this component, width could then return 0.
*/

import React, { HTMLAttributes, useContext, useMemo } from 'react'
import { useMeasure } from 'react-use'
import styled, { css } from 'styled-components'

import shouldForwardProp from 'js/lib/helpers/shouldForwardProp'
import globalStateContext from 'js/lib/withGlobalState/globalStateContext'

import ImageElement, { Props } from './Element'

interface WrapperProps extends HTMLAttributes<HTMLDivElement> {
    align: 'center' | 'right'
}

const Wrapper = styled.div.withConfig({
    shouldForwardProp: shouldForwardProp(['align']),
})<WrapperProps>`
    position: relative;
    display: flex;
    align-items: center;

    ${(p) =>
        p.align === 'right' &&
        css`
            justify-content: flex-end;
        `};

    ${(p) =>
        p.align === 'center' &&
        css`
            justify-content: center;
        `};
`

const Responsive = ({
    src,
    dimension,
    setNotFound,
    onLoad,
    alt,
    align,
    objectFit,
    objectPosition,
    showLoadingPlaceholder,
    isLoaded,
    ...rest
}: Props) => {
    const globalStateObject = useContext(globalStateContext) // TODO: Replace by SiteStore when it's fixed

    // eslint-disable-next-line
    const imageSizes = globalStateObject["globalState"].imageSizes

    const [ref, { width: boxWidth, height: boxHeight }] = useMeasure()

    const sizeParam = useMemo(() => {
        // Wait until box width is known
        if (!boxWidth) return

        const getSizeParameter = (width: number) =>
            imageSizes?.find((w: number) => width <= w) ?? 'full'

        const { width: imgWidth, height: imgHeight } = dimension ?? {}

        // If image covers the box and image dimensions are known
        if (
            objectFit === 'cover' &&
            [boxHeight, imgWidth, imgHeight].every(
                (val) => Number.isFinite(val) && val > 0,
            )
        ) {
            // Calculate new image height based on box width
            const newHeight = imgHeight / (imgWidth / boxWidth)

            // If new height is smaller than that of the box
            if (newHeight < boxHeight) {
                // Calculate new image width based on box height
                const newWidth = imgWidth / (imgHeight / boxHeight)
                return getSizeParameter(newWidth)
            }
        }

        return getSizeParameter(boxWidth)
    }, [boxWidth, boxHeight, dimension, objectFit, imageSizes])

    const hasParam = src.indexOf('?') !== -1
    const sourceLink = `${src}${sizeParam === 'full' ? '' : `${hasParam ? '&' : '?'}size=${sizeParam}`}`

    return (
        <Wrapper ref={ref} align={align} {...rest}>
            {/* Wait until size parameter is known before loading the image */}
            {sizeParam && (
                <ImageElement
                    src={sourceLink}
                    setNotFound={setNotFound}
                    onLoad={onLoad}
                    alt={alt}
                    objectFit={objectFit}
                    objectPosition={objectPosition}
                    isLoaded={isLoaded}
                    showLoadingPlaceholder={showLoadingPlaceholder}
                />
            )}
        </Wrapper>
    )
}

export default Responsive
